import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { ActionDock } from '../common/action-dock/ActionDock';
import { DOCK_ICON_COLOR } from '../common/action-dock/constants';

export interface CompanyPageBottomBarProps {
  completionPercent: number;
  onSetupPress: () => void;
  onAddPress: () => void;
  onPreviewPress: () => void;
  onAnalyticsPress: () => void;
  onSettingsPress: () => void;
  /**
   * fixed: centered in the viewport.
   * content: centered inside the nearest positioned content pane.
   */
  placement?: 'fixed' | 'content';
}

/**
 * Company Page builder dock — the same visual system as the /cv bottom bar
 * (ActionDock), wired to the builder: setup ring → checklist, Add / Preview /
 * Analytics / Settings items. No Enhance composer here: on web the employer AI
 * chat column already sits beside the dock, so handing a prompt to it from the
 * bar would be a second door to the same room. Native keeps the composer.
 */
export function CompanyPageBottomBar({
  completionPercent,
  onSetupPress,
  onAddPress,
  onPreviewPress,
  onAnalyticsPress,
  onSettingsPress,
  placement = 'fixed',
}: CompanyPageBottomBarProps) {
  const { theme, isDark } = useTheme();
  const { t } = useTranslation('common');
  const iconColor = isDark ? theme.text : DOCK_ICON_COLOR;
  const safePercent = Math.max(0, Math.min(100, Math.round(completionPercent)));
  const iconStyle = { color: iconColor };

  return (
    <ActionDock
      placement={placement}
      ring={{
        completionPercent,
        label: t('publicProfile.company.dock.setupLabel'),
        a11yLabel: t('publicProfile.company.dock.setupProgress', {
          percent: safePercent,
        }),
        a11yHint: t('publicProfile.company.dock.setupOpenHint'),
        onPress: onSetupPress,
      }}
      items={[
        {
          id: 'add',
          icon: <MaterialSymbol name="add" size={18} style={iconStyle} />,
          label: t('publicProfile.company.dock.add'),
          onPress: onAddPress,
        },
        {
          id: 'preview',
          icon: (
            <MaterialSymbol name="visibility" size={18} style={iconStyle} />
          ),
          label: t('publicProfile.company.dock.preview'),
          onPress: onPreviewPress,
        },
        {
          id: 'analytics',
          icon: (
            <MaterialSymbol name="bar_chart" size={18} style={iconStyle} />
          ),
          label: t('publicProfile.company.dock.analytics'),
          onPress: onAnalyticsPress,
        },
        {
          id: 'settings',
          icon: <MaterialSymbol name="settings" size={18} style={iconStyle} />,
          label: t('publicProfile.company.dock.settings'),
          onPress: onSettingsPress,
        },
      ]}
    />
  );
}
